import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchOrderLineItemCopies } from '../../api/orders/fetchOrderLineItemCopies';
import { GlobalReactQueryOptions } from '../../config';
import { Article } from '../../../models/Article';
import { UsageCondition } from '../../../models/Product';
import { LineItemCopyOut } from '../../store/api';
import { UseQueryResult } from '../../api/query-types';

type UseOrderLineItemCopies = UseQueryResult<'copies', ReadonlyArray<LineItemCopyOut>>;

type Options = {
    readonly orderId: string;
    readonly productId: string;
    readonly usageCondition: UsageCondition;
    readonly fetchFunc?: typeof fetchOrderLineItemCopies;
};

const EMPTY_RESPONSE = { copies: [] };

export const useOrderLineItemCopies = ({
    orderId,
    productId,
    usageCondition,
    fetchFunc = fetchOrderLineItemCopies
}: Options): UseOrderLineItemCopies => {
    const { data: { copies } = EMPTY_RESPONSE, ...result } = useQuery(
        ['getOrderDetailsLineItemCopies', orderId, productId],
        () => fetchFunc(orderId, productId),
        {
            ...GlobalReactQueryOptions
        }
    );

    const filteredCopies = useMemo(() => copies.filter(hasUsageCondition(usageCondition)), [copies, usageCondition]);

    return {
        ...result,
        copies: filteredCopies
    };
};

function hasUsageCondition(usageCondition: UsageCondition | undefined) {
    if (!usageCondition) {
        return () => true;
    }

    return ({ price = {} }: Article) => {
        return (
            usageCondition.lendingModel === price.lendingModel &&
            usageCondition.usageLimit === price.usageLimit &&
            usageCondition.termLimit === price.termLimit
        );
    };
}
